
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import { ActionTypes } from '@/store/actionTypes';
import { MutationTypes } from '@/store/mutationTypes';
import AlreadyHaveTrailerAttached from '@/components/AlreadyHaveTrailerAttached.vue';
import EnterTrailerOverlay from './EnterTrailerOverlay.vue';
import LoadingModal from '@/components/LoadingModal.vue';
import ScanTrailer from '@/components/ScanTrailer.vue';
import SomethingWrongOverlay from '@/components/SomethingWrongOverlay.vue';
import TrailerLookup from '@/components/TrailerLookup.vue';
import TrailerPlateVinVerification from '@/components/TrailerPlateVinVerification.vue';
import UnsupportedTrailerTypeOverlay from '@/components/UnsupportedTrailerTypeOverlay.vue';
import UnassociatedQrCodeOverlay from '@/components/UnassociatedQrCodeOverlay.vue';
import ReportQrUnassociated from '@/components/ReportQrUnassociated.vue';
import checkTrailerAttachability from '@/shared/mixins/checkTrailerAttachability';
import getTrailersAlreadyAttachedToTruck from '@/shared/mixins/getTrailersAlreadyAttachedToTruck';
import { cleanTrailerNumber, isNullOrUndefined } from '@/shared/helpers';
import Trailer from '@/types/Trailer';
import FbaApiError from '@/types/FbaApiError';
import { TrailerSelectionMethod } from '@/types/TrailerSelectionMethod';
import queries from '@/queries';
import globalConstants from '@/shared/globalConstants';
import useCameraPermission from '@/shared/composables/useCameraPermission';
import usePermissionAlert from '@/shared/composables/usePermissionAlert';
import ReturnOptions from '@/components/ReturnOptions.vue';
import DriverLoaded from '@/components/DriverLoaded.vue';

export default defineComponent({
    name: 'EnterTrailerCard',
    components: {
        AlreadyHaveTrailerAttached,
        EnterTrailerOverlay,
        LoadingModal,
        ScanTrailer,
        ReportQrUnassociated,
        SomethingWrongOverlay,
        TrailerLookup,
        TrailerPlateVinVerification,
        UnsupportedTrailerTypeOverlay,
        UnassociatedQrCodeOverlay,
        ReturnOptions,
        DriverLoaded,
    },
    computed: {
        ...mapGetters({
            cameraInfo: 'getDeviceCameraInfo',
            trailer: 'selectedTrailer',
            truck: 'getTruck',
            driver: 'getDriver',
            error: 'getError',
            isDetachError: 'isDetachError',
            deviceInfo: 'getDeviceInfo',
        }),
        alreadyAttached(): boolean {
            return (
                !isNullOrUndefined(this.currentAttachedTrailers) &&
                this.currentAttachedTrailers.length > 0
            );
        },
        getCompany(): string {
            return this.$store.getters.getCompany || '';
        },
        hasError(): boolean {
            return this.error instanceof FbaApiError;
        },
        driverCompany(): string {
            return this.$route.params.company as string;
        },
        showUnsupportedTypeOverlay(): boolean {
            return (
                this.hasError &&
                (this.error as FbaApiError).reasonCode ===
                    globalConstants.reasonCodes.invalidTrailerType
            );
        },
        showReportQrOverlay(): boolean {
            // QR exists but made it through shop install process without successfully saving the trailerId to the QR itself

            return (
                this.hasError &&
                (this.error as FbaApiError).reasonCode ===
                    globalConstants.reasonCodes.QrNotAssociated
            );
        },
        showUnassociatedQrOverlay(): boolean {
            // Handles both: QR doesn not exist OR associated trailer is deleted or does not exist in elastic

            const isReasonCode =
                (this.error as FbaApiError)?.reasonCode ===
                    globalConstants.reasonCodes.trailerNotFound ||
                (this.error as FbaApiError)?.reasonCode ===
                    globalConstants.reasonCodes.invalidQr;

            return this.hasError && isReasonCode;
        },
        trailerFromRoute(): string {
            return this.$route.params.trailerNumber as string;
        },
        isCameraAvailable(): boolean {
            return (
                this.cameraPermissionGranted ||
                this.isCameraPermissionPrompt ||
                this.showScanner
            );
        },
        showCameraSettings(): boolean {
            return (
                !this.isCameraAvailable ||
                this.cameraInfo?.isCameraShared === false
            );
        },
    },
    mixins: [getTrailersAlreadyAttachedToTruck, checkTrailerAttachability],
    setup() {
        const {
            cameraPermission,
            isCameraPermissionGranted,
            isCameraPermissionPrompt,
            isCameraPermissionDenied,
            setHasAccessedCameraView,
            setIsCameraShared,
        } = useCameraPermission();
        const { setPermissionAlert } = usePermissionAlert();

        return {
            cameraPermission,
            isCameraPermissionGranted,
            isCameraPermissionPrompt,
            isCameraPermissionDenied,
            setHasAccessedCameraView,
            setPermissionAlert,
            setIsCameraShared,
        };
    },
    data: (): {
        attachabilityLoading: boolean;
        cameraPermissionGranted: boolean;
        currentAttachedTrailers: Array<Trailer> | null;
        isCompanyMismatch: boolean;
        isInputText: boolean;
        isScannerPaused: boolean;
        showConfirm: boolean;
        showLoading: boolean;
        showScanner: boolean;
        scannedQrId: string | null;
        trailerId: string;
        verifyPlateVin: boolean;
        detachModalHidden: boolean;
        showLoadedTrailerModal: boolean;
        currentAttachedTrailerNumber: string | null;
        currentAttachedTrailerCompany: string | null;
    } => {
        return {
            attachabilityLoading: false,
            cameraPermissionGranted: false,
            currentAttachedTrailers: [],
            isCompanyMismatch: false,
            isInputText: false,
            isScannerPaused: false,
            showConfirm: false,
            showLoading: false,
            showScanner: true,
            scannedQrId: null,
            trailerId: '',
            verifyPlateVin: false,
            detachModalHidden: false,
            showLoadedTrailerModal: false,
            currentAttachedTrailerNumber: null,
            currentAttachedTrailerCompany: null,
        };
    },
    methods: {
        searchForTrailers() {
            this.$router.push({ name: 'DriverLanding' });
        },
        onCloseScanner() {
            this.$router.back();
        },
        onBackToScan() {
            this.verifyPlateVin = false;
            this.showConfirm = false;
            this.isCompanyMismatch = false;
            this.onResumeScanner();
        },
        onCompanyMismatch() {
            this.isCompanyMismatch = true;
        },
        hideBackButton(hide: boolean) {
            this.$emit('hideBackButton', hide);
        },
        onPauseScanner() {
            this.isScannerPaused = true;
        },
        onResumeScanner() {
            this.isScannerPaused = false;
        },
        detachErrorOccurred() {
            this.currentAttachedTrailers = [];
            this.showConfirm = false;
            this.onResumeScanner();
        },
        confirmTrailerResult(detachSucceeded?: boolean): void {
            if (detachSucceeded === false) {
                this.detachErrorOccurred();
                return;
            }

            this.showConfirm = true;

            this.currentAttachedTrailers = [];
        },
        acceptConfirm(): void {
            this.onToggleScanner(false);
            if (!isNullOrUndefined(this.trailer)) {
                if (this.trailerViolatesProximityEvaluation) {
                    this.showConfirm = false;
                    this.verifyPlateVin = true;
                    return;
                }

                this.routeToTrailerAssessment();
            }
        },
        rejectConfirm(): void {
            this.showConfirm = false;
            this.displayScanner();
            this.onResumeScanner();
        },
        routeToTrailerAssessment() {
            this.$router.push({
                name: 'TrailerAssessment',
                params: { trailerNumber: this.trailer.trailerNumber },
            });
        },
        trailerLocationVerified() {
            this.verifyPlateVin = false;
            this.routeToTrailerAssessment();
        },
        displayScanner() {
            if (this.cameraPermissionGranted) {
                this.onToggleScanner(true);
            }
        },
        onToggleScanner(value: boolean) {
            this.showScanner = value;
        },
        closeErrorModal(): void {
            this.onResumeScanner();
            this.$store.dispatch(ActionTypes.CLEAR_ERROR, undefined);
        },
        hideAttachModal(): void {
            this.detachModalHidden = true;
        },
        navigateToDriverLanding(): void {
            this.$store.dispatch(ActionTypes.CLEAR_ERROR, undefined);
            this.$router.push({ name: 'DriverLanding' });
        },
        onScanError(): void {
            this.onToggleScanner(false);
        },
        validateCompany(trailer: Trailer): boolean {
            return this.driverCompany !== trailer.company;
        },
        async onManualAttach() {
            this.$store.dispatch(ActionTypes.CLEAR_ERROR, undefined);
            this.onResumeScanner();
            this.onToggleScanner(false);
        },
        async onScanned(scannedQrId: string) {
            this.scannedQrId = scannedQrId;
            this.onPauseScanner();
            const trailer = await this.queryTrailerQr(scannedQrId);
            if (trailer instanceof Trailer) {
                const isCompanyMismatch = this.validateCompany(trailer);
                if (isCompanyMismatch) this.onCompanyMismatch();
                this.onTrailerResult(trailer, TrailerSelectionMethod.qrScan);
            }
        },
        async onSearchTrailer(trailerId: string) {
            this.showLoading = true;
            const trailer = await this.queryTrailer(trailerId);
            if (trailer instanceof Trailer) {
                await this.onTrailerResult(
                    trailer,
                    TrailerSelectionMethod.manual
                );
            }
            this.showLoading = false;
        },
        async onTrailerResult(
            trailer: Trailer,
            trailerSelectionMethod: TrailerSelectionMethod
        ): Promise<void> {
            if (!isNullOrUndefined(trailer) && trailer instanceof Trailer) {
                this.$store.dispatch(ActionTypes.SET_SELECTED_TRAILER, {
                    trailer,
                    trailerSelectionMethod,
                });

                const attachedTrailers =
                    await this.getTrailersAlreadyAttachedToTruck();
                await this.checkAttachability();

                // Loaded messaging is priority over currently attached
                if (this.trailerAndDriverAreLoaded) {
                    this.currentAttachedTrailers = attachedTrailers;
                    this.currentAttachedTrailerNumber = attachedTrailers
                        ? attachedTrailers[0]?.trailerNumber
                        : null;
                    this.currentAttachedTrailerCompany = attachedTrailers
                        ? attachedTrailers[0]?.company
                        : null;

                    this.showLoadedTrailerModal = true;
                    return;
                }

                if (attachedTrailers !== null) {
                    this.currentAttachedTrailers = attachedTrailers;
                    this.detachModalHidden = false;
                    return;
                }

                this.confirmTrailerResult();
            }
        },
        async checkAttachability() {
            this.attachability = null;
            this.attachabilityLoading = true;
            await this.checkTrailerAttachability({
                trailerNumber: this.trailer?.trailerNumber,
                trailerCompany: this.trailer?.company,
                scannedQrId: this.trailer?.trailerQr,
                driverId: this.driver.id,
            });
            this.attachabilityLoading = false;
        },
        async queryTrailer(trailerId: string): Promise<Trailer | FbaApiError> {
            const cleanedId = cleanTrailerNumber(trailerId);

            const trailer = await queries.getTrailerById(
                cleanedId,
                this.driverCompany
            );
            if (!isNullOrUndefined(trailer) && trailer instanceof Trailer) {
                return trailer;
            } else
                this.$store.commit(
                    MutationTypes.SET_ERROR,
                    trailer as FbaApiError
                );

            return trailer;
        },
        async queryTrailerQr(
            scannedQrId: string
        ): Promise<Trailer | FbaApiError> {
            const trailer = await queries.getTrailerByQrId(scannedQrId);

            if (!isNullOrUndefined(trailer) && trailer instanceof Trailer) {
                return trailer;
            } else {
                this.$store.commit(
                    MutationTypes.SET_ERROR,
                    trailer as FbaApiError
                );
            }

            return trailer;
        },
    },
    async mounted() {
        this.$store.dispatch(ActionTypes.CLEAR_ERROR, undefined);
        this.setHasAccessedCameraView(true);
        if (this.alreadyAttached) this.detachModalHidden = false;
    },
    watch: {
        cameraPermission: {
            handler() {
                this.setHasAccessedCameraView(true);

                if (!isNullOrUndefined(this.cameraPermission)) {
                    this.cameraPermissionGranted =
                        this.isCameraPermissionGranted;
                    const promptPermission = this.isCameraPermissionPrompt;
                    this.showScanner =
                        promptPermission || this.cameraPermissionGranted;

                    if (this.isCameraPermissionDenied) {
                        this.setIsCameraShared(false);
                        this.setPermissionAlert();
                    } else if (this.isCameraPermissionGranted) {
                        this.setIsCameraShared(true);
                    }

                    return;
                }

                this.showScanner = false;
                this.cameraPermissionGranted = false;
            },
            immediate: true,
        },
    },
});
